import { Location } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { faArrowLeft, faArrowRight, faBarcode, faBox, faCheck, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { forkJoin } from 'rxjs';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { InventoryService } from '../../../pick-plan/services/inventory.service';
import { HandlingUnitDetailDTO } from '../../models/handling-unit-detail';
import { HandlingUnitMasterDTO, handlingUnitLocation } from '../../models/handling-unit-master';
import { InventoryScanPopupComponent, InventoryScanPopupData } from '../inventory-summary/inventory-scan-popup/inventory-scan-popup.component';
import { InventoryTransferService } from './inventory-transfer.service';

/** A destination line as it will look once the transfer is saved. */
interface TransferPreviewRow {
  hud: HandlingUnitDetailDTO;
  current: number | null;
  after: number;
  delta: number;
}

@Component({
  selector: 'inventory-transfer',
  templateUrl: './inventory-transfer.component.html',
  styleUrls: ['./inventory-transfer.component.scss']
})
export class InventoryTransferComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  public isLoading = false;

  public faArrowLeft = faArrowLeft;
  public faArrowRight = faArrowRight;
  public faBarcode = faBarcode;
  public faBox = faBox;
  public faCheck = faCheck;
  public faLocationDot = faLocationDot;

  public hum: HandlingUnitMasterDTO;
  public destinationHum: HandlingUnitMasterDTO;
  public attributeNames: string[] = [];
  public preview: TransferPreviewRow[] = [];

  constructor(
    public inventoryTransferService: InventoryTransferService,
    private router: Router,
    public location: Location,
    public dialog: MatDialog,
    public toastr: NbToastrService,
    private inventoryService: InventoryService,
    private errorService: ErrorService,
  ) {

  }

  ngOnInit() {
    if (!this.inventoryTransferService.sourceHum) {
      this.location.back();
      return;
    }
    this.hum = this.inventoryTransferService.sourceHum;
    // Attribute columns follow the first item, as the old grid did.
    this.attributeNames = (this.hum.handlingUnitDetails?.[0]?.itemAttributeValues ?? []).map(x => x.name);

  }

  setQty(hud: HandlingUnitDetailDTO) {
    if (hud?.packageHasComponents) {
      const componentToPackage = (hud.transferComponentQty || 0) / hud.packageComponentQuantityPerPackage;
      hud.transferQty = componentToPackage + (hud.transferPackageQty || 0);
    }
    this.buildPreview();
  }

  getLocation(hum: HandlingUnitMasterDTO): string {
    return handlingUnitLocation(hum);
  }

  itemCount(hum: HandlingUnitMasterDTO): string {
    const count = hum?.handlingUnitDetails?.length ?? 0;
    return `${count} ${count === 1 ? 'item' : 'items'}`;
  }

  /** Variety and attribute values on one line, for the stacked mobile rows. */
  describe(hud: HandlingUnitDetailDTO): string {
    return [hud.varietyName, ...(hud.itemAttributeValues ?? []).map(x => x.value)].filter(Boolean).join(' · ');
  }

  get transferLines(): HandlingUnitDetailDTO[] {
    return (this.hum?.handlingUnitDetails ?? []).filter(x => x.transferQty);
  }

  get canComplete(): boolean {
    return !!this.destinationHum && this.transferLines.length > 0;
  }

  get summary(): string {
    const lines = this.transferLines;
    if (!this.destinationHum)
      return 'Scan a destination and enter quantities to transfer';
    if (!lines.length)
      return 'Enter quantities to transfer';
    const total = lines.reduce((sum, x) => sum + x.transferQty, 0);
    return `Transferring ${total.toLocaleString()} from ${lines.length} ${lines.length === 1 ? 'line' : 'lines'} to ${this.destinationHum.barcode}`;
  }

  trackByHud(_: number, hud: HandlingUnitDetailDTO): number | undefined {
    return hud.id;
  }

  trackByPreview(index: number): number {
    return index;
  }

  /** Destination lines after the transfer; matches lines the same way save() does. */
  private buildPreview() {
    if (!this.destinationHum) {
      this.preview = [];
      return;
    }
    const sameLine = (a: HandlingUnitDetailDTO, b: HandlingUnitDetailDTO) => a.lotNumber == b.lotNumber && a.itemName == b.itemName;
    const lines = this.transferLines;

    const existing = (this.destinationHum.handlingUnitDetails ?? []).map(hud => {
      const delta = lines.filter(x => sameLine(x, hud)).reduce((sum, x) => sum + x.transferQty, 0);
      return { hud, current: hud.quantity, after: hud.quantity + delta, delta };
    });
    const added = lines
      .filter(x => !this.destinationHum.handlingUnitDetails?.some(d => sameLine(x, d)))
      .map(hud => ({ hud, current: null, after: hud.transferQty, delta: hud.transferQty }));

    this.preview = [...existing, ...added];
  }

  scan() {
    const dialogRef = this.dialog.open(InventoryScanPopupComponent, {
      minWidth: '30%',
      data: <InventoryScanPopupData>{
        isTransferScan: true

      }
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
      if (x?.isHum) {
        if (x.hum.id == this.hum.id) {
          this.toastr.warning("Inventory cannot be transfered to the selected item", "Invalid Scan");
        } else {
          this.destinationHum = x.hum;
          this.buildPreview();
        }
      }
    });
  }

  save() {

    let t = 0;

    // update the source
    for (let sourceHud of this.hum.handlingUnitDetails) {

      if (sourceHud.transferQty && sourceHud.transferQty != 0 && sourceHud.transferQty != undefined && sourceHud.transferQty != null) {
        sourceHud.quantity = sourceHud.quantity - sourceHud.transferQty;

        //update the destination
        let existingDestHud = this.destinationHum.handlingUnitDetails.find(x => x.lotNumber == sourceHud.lotNumber && x.itemName == sourceHud.itemName);
        
        
        if (existingDestHud) {
          existingDestHud.quantity = existingDestHud.quantity + sourceHud.transferQty;
        } else {
          let newHud: HandlingUnitDetailDTO = {
            isActive: true,
            itemId: sourceHud.itemId,
            itemDescription: sourceHud.itemDescription,
            itemName: sourceHud.itemName,
            loadId: sourceHud.loadId,
            loadNumber: sourceHud.loadNumber,
            lotId: sourceHud.lotId,
            lotNumber: sourceHud.lotNumber,
            quantity: sourceHud.transferQty

          }
          this.destinationHum.handlingUnitDetails.push(newHud);
        }

      }


    }

      // hit the API

      this.isLoading = true;

      let requestList:any = [];

      if (this.destinationHum.id != 0 && this.destinationHum.id) {
        requestList.push(this.inventoryService.putInventoryHUM(this.destinationHum));
      }
      else {
        requestList.push(this.inventoryService.postInventoryHUM(this.destinationHum));
      }

      requestList.push(this.inventoryService.putInventoryHUM(this.hum));

      forkJoin(requestList).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:(results:any) => {
        this.isLoading = false;
        this.router.navigateByUrl("/harvest-engine/inventory/inventory-summary-popup?humId=" + results[0].id);
      }, error:err => { this.errorService.handleError(err); this.isLoading = false; }})

  }
}
